/**
 * Riftgate Arena — lazy loading of the game's UI string catalogues.
 *
 * ── WHY THESE ARE BUNDLE CHUNKS AND NOT AN ENDPOINT ─────────────────────────
 * The task allowed either a game-side `/game/i18n/:locale` route or static
 * assets from the frontend build. Static wins on every axis that matters here:
 *
 *   - UI strings version with the UI that uses them. A key added to a screen
 *     and its translation ship in the same commit and the same deploy; there is
 *     no window in which the running frontend asks for a key the backend has
 *     never heard of.
 *   - Vite already emits one content-hashed chunk per `import()`ed JSON, so the
 *     files are immutable-cacheable at the CDN and cost the backend nothing —
 *     no route, no DB, no cache invalidation, no Nest module.
 *   - It works with the backend down, in preview builds, and in `vitest`.
 *
 * The one thing an endpoint would buy — changing copy without a deploy — we do
 * not want: copy that can change without review is how a legal string drifts
 * out of sync with the consent version recorded against it.
 *
 * ── WHY A LITERAL MAP OF THUNKS ─────────────────────────────────────────────
 * `import(`./locales/${locale}.json`)` looks tidier and is a trap: a template
 * literal makes the import unanalysable, so the bundler either bundles EVERY
 * locale into one chunk (blowing the 150 KB shell budget this split exists to
 * protect) or emits a glob it cannot tree-shake. Eight explicit thunks are
 * eight statically-known dynamic edges — exactly one of which is ever fetched.
 */
import type { Catalogue } from './translate';
import { SUPPORTED_LOCALES, type Locale } from './locales';

/** The reserved key holding a file's provenance. Never rendered. */
export const META_KEY = '_meta';

/**
 * How a catalogue came to exist. This is a HONESTY flag, not a progress bar:
 * `machine` means nobody who speaks the language has read it.
 */
export type TranslationStatus = 'source' | 'reviewed' | 'machine' | 'placeholder';

export interface LocaleMeta {
  /** The locale this file is for; must equal its filename. */
  readonly locale: string;
  /** Native language name, duplicated here so a file is self-describing. */
  readonly name: string;
  readonly translationStatus: TranslationStatus;
  /** Who reviewed it. Required to be a real name when status is `reviewed`. */
  readonly reviewedBy: string | null;
  /** ISO date of the last content change. */
  readonly updated: string;
  /** Which catalogue it was translated from. */
  readonly sourceLocale?: string;
  /** Free-text caveat shown to nobody but read by whoever picks this up next. */
  readonly note?: string;
}

export type CatalogueLoader = (locale: Locale) => Promise<Catalogue>;

/**
 * One dynamic import per language. ORDER AND LITERALNESS MATTER — see above.
 */
const LOADERS: Readonly<Record<Locale, () => Promise<unknown>>> = Object.freeze({
  de: () => import('./locales/de.json'),
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
  ja: () => import('./locales/ja.json'),
  lv: () => import('./locales/lv.json'),
  'pt-BR': () => import('./locales/pt-BR.json'),
  ru: () => import('./locales/ru.json'),
  tr: () => import('./locales/tr.json'),
});

/** Parsed catalogues, so switching back to a language is free. */
const cache = new Map<Locale, Catalogue>();

/** In-flight loads, so two screens asking at once share one chunk fetch. */
const inflight = new Map<Locale, Promise<Catalogue>>();

function unwrap(module: unknown): unknown {
  // A JSON module is `{ default: {...} }` through Vite and through `node --test`
  // with a JSON import attribute, but a raw object when a test injects one.
  if (module !== null && typeof module === 'object' && 'default' in module) {
    return (module as { default: unknown }).default;
  }
  return module;
}

/** A corrupt or empty catalogue is a deploy bug — fail loudly, never silently. */
export function assertCatalogue(value: unknown, locale: string): Catalogue {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`i18n: catalogue for "${locale}" is not an object`);
  }
  const entries = Object.keys(value as object).filter((k) => k !== META_KEY);
  if (entries.length === 0) {
    throw new Error(`i18n: catalogue for "${locale}" has no messages`);
  }
  return value as Catalogue;
}

/** Load (and cache) one language's UI strings. */
export const loadGameCatalogue: CatalogueLoader = async (locale) => {
  const cached = cache.get(locale);
  if (cached !== undefined) return cached;

  const pending = inflight.get(locale);
  if (pending !== undefined) return pending;

  const load = LOADERS[locale];
  if (load === undefined) throw new Error(`i18n: no catalogue for locale "${locale}"`);

  const promise = load()
    .then((module) => {
      const catalogue = assertCatalogue(unwrap(module), locale);
      cache.set(locale, catalogue);
      return catalogue;
    })
    .finally(() => {
      inflight.delete(locale);
    });

  inflight.set(locale, promise);
  return promise;
};

/** Has this catalogue already been fetched and parsed? */
export function isCatalogueLoaded(locale: Locale): boolean {
  return cache.has(locale);
}

/** Drop parsed catalogues. Tests only — the browser keeps the chunk cached. */
export function clearCatalogueCache(): void {
  cache.clear();
  inflight.clear();
}

const FALLBACK_META: LocaleMeta = {
  locale: 'unknown',
  name: 'unknown',
  translationStatus: 'placeholder',
  reviewedBy: null,
  updated: 'unknown',
};

/**
 * Read a catalogue's `_meta` block.
 *
 * Never throws: a settings screen asking "is this language machine-translated?"
 * must get an answer even from a file that predates the flag, and the safe
 * answer is the pessimistic one.
 */
export function readLocaleMeta(catalogue: Catalogue | null | undefined): LocaleMeta {
  const raw = catalogue?.[META_KEY];
  if (raw === undefined || typeof raw !== 'object' || raw === null) return FALLBACK_META;
  const node = raw as Record<string, unknown>;
  const status = node['translationStatus'];
  return {
    locale: typeof node['locale'] === 'string' ? (node['locale'] as string) : FALLBACK_META.locale,
    name: typeof node['name'] === 'string' ? (node['name'] as string) : FALLBACK_META.name,
    translationStatus: isTranslationStatus(status) ? status : 'placeholder',
    reviewedBy: typeof node['reviewedBy'] === 'string' ? (node['reviewedBy'] as string) : null,
    updated: typeof node['updated'] === 'string' ? (node['updated'] as string) : FALLBACK_META.updated,
    ...(typeof node['sourceLocale'] === 'string'
      ? { sourceLocale: node['sourceLocale'] as string }
      : {}),
    ...(typeof node['note'] === 'string' ? { note: node['note'] as string } : {}),
  };
}

function isTranslationStatus(value: unknown): value is TranslationStatus {
  return (
    value === 'source' || value === 'reviewed' || value === 'machine' || value === 'placeholder'
  );
}

/** Every locale that has a loader — the runtime's proof the registry is honest. */
export function loadableLocales(): readonly Locale[] {
  return SUPPORTED_LOCALES.filter((locale) => LOADERS[locale] !== undefined);
}
